import { Link } from "react-router-dom";
import { useMemo, useState } from "react";
import NewNavbar from "../components/NewNavbar";
import ProductCard from "../components/ProductCard";
import Footer from "../components/Footer.js";
import { lifestyleImages } from "../data/mockData";
import {
  useGetProductsAllProductsQuery,
  usePublicProductsAllListQuery,
} from "../store/api.ts";
import SaveToBoardModal from "../components/SaveToBoardModal.js";
import toast from "react-hot-toast";
import {getVisibleCardMatchPercentage} from "./../utils.js";
import SEO from "../components/SEO";

const ALL_PRODUCTS_PAGE = 1;
const ALL_PRODUCTS_PAGE_SIZE = 1000;

const normalizeProductForCard = (product) => {
  const score =
    product?.product_score !== null && product?.product_score !== undefined
      ? Math.round(Number(product.product_score))
      : null;

  return {
    ...product,
    id: product?.id,
    slug: product?.slug || product?.product_url || "",
    real_slug: product?.slug || product?.product_url || "",
    name: product?.name || product?.product_name || "",
    brand:
      product?.brand ||
      product?.distributor ||
      product?.name ||
      product?.product_name ||
      "",
    price: product?.price ?? "",
    original_price: product?.price ?? "",
    discounted_price: product?.discounted_price ?? "",
    discountedPrice: product?.discounted_price ?? "",
    discount: Number(product?.discount ?? 0),
    discountPercent: Number(product?.discount ?? 0),
    image: product?.image || product?.image_url || "",
    image_url: product?.image_url || product?.image || "",
    matchPercentage: score,
    productType: "product",
    product_type: "product",
    is_marketplace: false,
  };
};

const ProductCardLoader = ({ imageClassName = "h-[520px]" }) => {
  return (
    <div className="animate-pulse">
      <div className={`w-full bg-[#E8E4DF] ${imageClassName}`} />

      <div className="space-y-2 py-3">
        <div className="h-3 w-2/3 rounded bg-[#E8E4DF]" />
        <div className="h-3 w-1/3 rounded bg-[#E8E4DF]" />
      </div>
    </div>
  );
};

const EditorialProductCard = ({
  item,
  imageClassName = "h-[520px]",
  onSaveBoard,
  isSavingBoard,
  isLoading = false,
}) => {
  if (isLoading) {
    return <ProductCardLoader imageClassName={imageClassName} />;
  }
  if (!item) return null;

  return (
    <ProductCard
      item={item}
      imageHeightClass={imageClassName}
      matchPercentage={item.matchPercentage}
      compactActions
      onSaveBoard={onSaveBoard}
      isSavingBoard={isSavingBoard}
    />
  );
};
const LifestyleImage = ({ src, className = "" }) => {
  return (
    <div className={`overflow-hidden bg-[#E8E4DF] ${className}`}>
      <img src={src} alt="" className="h-full w-full object-cover" />
    </div>
  );
};

const CollectionLandingPage = () => {
  const authToken = localStorage.getItem("authToken");
  const isAuthenticated = Boolean(authToken);

  const [saveBoardProduct, setSaveBoardProduct] = useState(null);
  const [isSaveBoardModalOpen, setIsSaveBoardModalOpen] = useState(false);
  const signedInQuery = useGetProductsAllProductsQuery(
    {
      page: ALL_PRODUCTS_PAGE,
      pageSize: ALL_PRODUCTS_PAGE_SIZE,
    },
    {
      skip: !isAuthenticated,
      refetchOnMountOrArgChange: false,
    },
  );

  const publicQuery = usePublicProductsAllListQuery(
    {
      page: ALL_PRODUCTS_PAGE,
      pageSize: ALL_PRODUCTS_PAGE_SIZE,
    },
    {
      skip: isAuthenticated,
      refetchOnMountOrArgChange: false,
    },
  );

  const activeQuery = isAuthenticated ? signedInQuery : publicQuery;
  const isProductsLoading = activeQuery.isLoading || activeQuery.isFetching;

  const products = useMemo(() => {
    if (!Array.isArray(activeQuery?.data?.results)) return [];

    return activeQuery.data.results.map(normalizeProductForCard);
  }, [activeQuery?.data]);

  const handleSaveBoard = (product) => {
    if (!isAuthenticated) {
      toast.error("Please sign in to save this product.");
      return;
    }

    if (!product) {
      toast.error("Product details are not available yet.");
      return;
    }

    const productSlug =
      product?.real_slug || product?.slug || product?.product_url || "";

    if (!productSlug) {
      toast.error("Product slug is not available yet.");
      return;
    }

    const visibleMatchPercentage = getVisibleCardMatchPercentage(product);

    setSaveBoardProduct({
      ...product,
      slug: productSlug,
      real_slug: productSlug,
      productType: "product",
      product_type: "product",
      is_marketplace: false,
      matchPercentage: visibleMatchPercentage,
      match_percentage: visibleMatchPercentage,
      image: product?.image || product?.image_url || "",
      image_url: product?.image_url || product?.image || "",
      name: product?.name || product?.product_name || "Product",
    });

    setIsSaveBoardModalOpen(true);
  };

  return (
    <div className="min-h-screen bg-[#F6F4F1] text-black">
      <SEO
        title="Eveness Collection | Shop Fashion Pieces & Outfit Discovery"
        description="Discover your perfect style with the Eveness Collection. Shop curated styles, premium silhouettes & products designed for personalized fashion outfit discovery."
        path="/shop"
      />
      <NewNavbar bottomNav="none" />

      <main className="px-2 pb-10 pt-2 md:px-3">
        {/* Desktop hero */}
        <section className="hidden gap-2 md:grid md:grid-cols-3">
          {[0, 1, 2].map((index) => (
            <EditorialProductCard
              key={products[index]?.id ?? `top-product-loader-${index}`}
              item={products[index]}
              imageClassName="h-[610px]"
              onSaveBoard={handleSaveBoard}
              isSavingBoard={false}
              isLoading={isProductsLoading}
            />
          ))}
        </section>
        {/* Mobile hero */}
        <section className="grid gap-2 md:hidden">
          <LifestyleImage src={lifestyleImages[0]} className="h-[300px]" />

          <div className="grid grid-cols-2 gap-2">
            <EditorialProductCard
              item={products[2]}
              imageClassName="h-[300px]"
              onSaveBoard={handleSaveBoard}
              isSavingBoard={false}
              isLoading={isProductsLoading}
            />
            <EditorialProductCard
              item={products[1]}
              imageClassName="h-[300px]"
              onSaveBoard={handleSaveBoard}
              isSavingBoard={false}
              isLoading={isProductsLoading}
            />
          </div>
        </section>
        {/* Desktop heading */}
        <section className="hidden items-end gap-3 py-2 md:grid md:grid-cols-[1fr_1.7fr_1fr]">
          <div className="text-[24px] font-medium">
            Women&apos;s New Arrivals
          </div>

          <p className="self-end text-center font-inter text-[12px] font-medium leading-[1.35] text-[#000000]">
            Explore Eveness new womenswear arrivals: surprising pieces come
            together with ease, shaping a fluid style that adapts to the wearer
            and fits every moment.
          </p>

          <Link
            to="/shop/products"
            className="flex h-[42px] w-full items-center justify-center rounded-[5px] border border-[#313131] bg-[#313131] px-5 py-3 font-spaceMono text-[12px] font-normal text-white transition hover:bg-black"
          >
            View all products
          </Link>
        </section>{" "}
        {/* Desktop editorial grid */}
        <section className="hidden gap-2 md:grid md:grid-cols-12 mt-4">
          <div className="md:col-span-3">
            <EditorialProductCard
              item={products[3]}
              imageClassName="h-[490px]"
              onSaveBoard={handleSaveBoard}
              isSavingBoard={false}
            />
          </div>

          <LifestyleImage
            src={lifestyleImages[0]}
            className="md:col-span-9 md:h-[490px]"
          />

          <LifestyleImage
            src={lifestyleImages[1]}
            className="md:col-span-6 md:h-[660px]"
          />

          <div className="relative flex items-center justify-center bg-[#F6F4F1] md:col-span-6">
            <span className="absolute left-4 top-1/2 z-10 h-[27px] w-[27px] -translate-y-1/2 rounded-full bg-[#B3B3B3] md:left-[140px]" />
            <span className="absolute right-4 top-1/2 z-10 h-[27px] w-[27px] -translate-y-1/2 rounded-full bg-[#B3B3B3] md:right-[140px]" />
            <div className="relative w-full max-w-[320px]">
              <div className="mb-2 flex items-center justify-between font-inter text-[12px] md:text-[16px] font-normal">
                <span>{products[4]?.name || "Featured Product"}</span>
                <Link
                  to="/shop/products"
                  className="font-spaceMono font-bold text-[12px] underline"
                >
                  View All
                </Link>
              </div>

              <EditorialProductCard
                item={products[4]}
                imageClassName="h-[480px]"
                onSaveBoard={handleSaveBoard}
                isSavingBoard={false}
              />
            </div>
          </div>

          <LifestyleImage
            src={lifestyleImages[2]}
            className="md:col-span-5 md:h-[470px]"
          />

          <LifestyleImage
            src={products[5]?.image || lifestyleImages[0]}
            className="md:col-span-4 md:h-[470px]"
          />

          <div className="md:col-span-3">
            <EditorialProductCard
              item={products[0]}
              imageClassName="h-[470px]"
              onSaveBoard={handleSaveBoard}
              isSavingBoard={false}
            />
          </div>

          {[products[8], products[1], products[2], products[11]]
            .filter(Boolean)
            .map((product) => (
              <div key={`row-one-${product.id}`} className="md:col-span-3">
                <EditorialProductCard
                  item={product}
                  imageClassName="h-[470px]"
                  onSaveBoard={handleSaveBoard}
                  isSavingBoard={false}
                />
              </div>
            ))}

          {[products[3], products[9], products[10]]
            .filter(Boolean)
            .map((product) => (
              <div key={`row-two-${product.id}`} className="md:col-span-4">
                <EditorialProductCard
                  item={product}
                  imageClassName="h-[620px]"
                  onSaveBoard={handleSaveBoard}
                  isSavingBoard={false}
                />
              </div>
            ))}
        </section>
        {/* Mobile editorial layout */}
        <section className="grid gap-2 md:hidden">
          <EditorialProductCard
            item={products[3]}
            imageClassName="h-[560px]"
            onSaveBoard={handleSaveBoard}
            isSavingBoard={false}
          />

          <LifestyleImage src={lifestyleImages[1]} className="h-[245px]" />

          <div className="relative flex items-center justify-center bg-[#F6F4F1] py-8">
            <span className="absolute left-4 top-1/2 z-10 h-[27px] w-[27px] -translate-y-1/2 rounded-full bg-[#B3B3B3]" />
            <span className="absolute right-4 top-1/2 z-10 h-[27px] w-[27px] -translate-y-1/2 rounded-full bg-[#B3B3B3]" />

            <div className="relative w-full max-w-[270px]">
              <div className="mb-2 flex items-center justify-between font-inter text-[9px]">
                <span>{products[4]?.name || "Featured Product"}</span>
                <Link to="/shop/products" className="underline">
                  View All
                </Link>
              </div>

              <EditorialProductCard
                item={products[4]}
                imageClassName="h-[390px]"
                onSaveBoard={handleSaveBoard}
                isSavingBoard={false}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <LifestyleImage
              src={products[5]?.image || lifestyleImages[0]}
              className="h-[210px]"
            />
            <LifestyleImage src={lifestyleImages[2]} className="h-[210px]" />
          </div>

          <EditorialProductCard
            item={products[0]}
            imageClassName="h-[620px]"
            onSaveBoard={handleSaveBoard}
            isSavingBoard={false}
          />

          <div className="grid grid-cols-2 gap-2">
            <EditorialProductCard
              item={products[8]}
              imageClassName="h-[300px]"
              onSaveBoard={handleSaveBoard}
              isSavingBoard={false}
              isLoading={isProductsLoading}
            />
            <EditorialProductCard
              item={products[11]}
              imageClassName="h-[300px]"
              onSaveBoard={handleSaveBoard}
              isSavingBoard={false}
              isLoading={isProductsLoading}
            />
          </div>

          <EditorialProductCard
            item={products[6]}
            imageClassName="h-[600px]"
            onSaveBoard={handleSaveBoard}
            isSavingBoard={false}
          />

          <div className="grid grid-cols-2 gap-2">
            <EditorialProductCard
              item={products[1]}
              imageClassName="h-[320px]"
              onSaveBoard={handleSaveBoard}
              isSavingBoard={false}
              isLoading={isProductsLoading}
            />
            <EditorialProductCard
              item={products[2]}
              imageClassName="h-[320px]"
              onSaveBoard={handleSaveBoard}
              isSavingBoard={false}
              isLoading={isProductsLoading}
            />
          </div>
        </section>
      </main>
      <SaveToBoardModal
        isOpen={isSaveBoardModalOpen}
        product={saveBoardProduct}
        onClose={() => {
          setIsSaveBoardModalOpen(false);
          setSaveBoardProduct(null);
        }}
        onRequireAuth={() => {
          setIsSaveBoardModalOpen(false);
          setSaveBoardProduct(null);
          toast.error("Please sign in to save this product.");
        }}
        onSaved={() => {
          setSaveBoardProduct(null);
        }}
      />
      <Footer />
    </div>
  );
};

export default CollectionLandingPage;
